import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Link2, TriangleAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import type { CollectionItem } from '../../lib/types';
import { itemTitle, type Locale } from '../../lib/collections';

interface Props {
  relationTo: string;          // key de la colección destino (ej. 'agents')
  label?: string;              // nombre legible del campo (ej. 'Sucursal')
  value: string | string[];    // id(s) del/los collectionItem relacionados
  onChange: (value: string | string[]) => void;
  many?: boolean;              // admite varios (default: uno)
  locale?: Locale;
}

// Picker de relaciones intuitivo: busca por título del ítem destino, soporta
// uno o varios, permite desasignar/limpiar. Carga los candidatos del tipo
// `relationTo` una vez y filtra en cliente (las colecciones de PYME son chicas).
export function RelationField({ relationTo, label, value, onChange, many = false, locale = 'es' }: Props) {
  const navigate = useNavigate();
  const [options, setOptions] = useState<CollectionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedIds = useMemo<string[]>(
    () => (Array.isArray(value) ? value : value ? [value] : []),
    [value],
  );

  useEffect(() => {
    let alive = true;
    api.collections.list(relationTo)
      .then((items) => { if (alive) setOptions(items); })
      .catch((e: unknown) => {
        if (!alive) return;
        const msg = e instanceof Error ? e.message : '';
        // 404 = colección no activada en este tenant → tratar como lista vacía con CTA
        if (msg.toLowerCase().includes('not found') || msg.includes('404')) {
          setOptions([]);
        } else {
          setError('No se pudieron cargar las opciones relacionadas.');
        }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [relationTo]);

  // Calcular posición del dropdown al abrir (portal sobre el body, evita overflow:hidden del modal).
  function openDropdown() {
    if (inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setDropdownRect({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    setOpen(true);
  }

  // Cerrar el dropdown al hacer click fuera.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      const inBox = boxRef.current?.contains(target);
      const inPortal = (document.getElementById('relation-portal'))?.contains(target);
      if (!inBox && !inPortal) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const byId = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options
      .filter((o) => !selectedIds.includes(o.id))
      .filter((o) => !q || itemTitle(o, locale).toLowerCase().includes(q))
      .slice(0, 8);
  }, [options, query, selectedIds, locale]);

  function pick(id: string) {
    if (many) {
      onChange([...selectedIds, id]);
    } else {
      onChange(id);
      setOpen(false);
    }
    setQuery('');
  }

  function unassign(id: string) {
    if (many) onChange(selectedIds.filter((x) => x !== id));
    else onChange('');
  }

  if (error) {
    return (
      <div className="flex items-start gap-2.5 rounded-lg border border-red-500/25 bg-red-500/8 px-3 py-2.5">
        <TriangleAlert size={14} className="text-red-400 shrink-0 mt-0.5" />
        <p className="text-sm text-ui-text-muted">No se pudo conectar. Intenta recargar la página.</p>
      </div>
    );
  }

  if (!loading && options.length === 0) {
    const fieldName = label ?? relationTo;
    return (
      <div className="flex items-start gap-2.5 rounded-lg border border-amber-500/25 bg-amber-500/8 px-3 py-2.5">
        <TriangleAlert size={14} className="text-amber-400 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-sm text-ui-text-muted leading-relaxed">
            Aún no hay opciones disponibles para <strong className="text-ui-text">{fieldName}</strong>.
            Crea una primero y después podrás seleccionarla aquí.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/c/${relationTo}`)}
            className="mt-1 text-sm text-ui-accent hover:opacity-80 transition-opacity"
          >
            Crear una opción →
          </button>
        </div>
      </div>
    );
  }

  // Para campo de uno: si ya hay selección y no está abierto, mostramos el chip.
  const showInput = many || selectedIds.length === 0 || open;

  return (
    <div ref={boxRef} className="relative space-y-2">
      {/* Seleccionados */}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedIds.map((id) => {
            const it = byId.get(id);
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 bg-ui-accent/15 border border-ui-accent/30 rounded-md text-sm text-ui-text"
              >
                <Link2 size={12} className="text-ui-accent shrink-0" />
                {it ? itemTitle(it, locale) : <span className="text-ui-text-subtle italic">id desconocido</span>}
                <button
                  type="button"
                  onClick={() => unassign(id)}
                  title="Desasignar"
                  className="p-0.5 text-ui-text-muted hover:text-ui-danger rounded"
                >
                  <X size={13} />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Buscador */}
      {showInput && (
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-text-subtle pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); openDropdown(); }}
            onFocus={() => openDropdown()}
            placeholder={loading ? 'Cargando…' : (many ? 'Buscar para añadir…' : 'Buscar y asignar…')}
            disabled={loading}
            className="w-full bg-ui-bg border border-ui-border rounded-md pl-9 pr-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent focus:ring-1 focus:ring-ui-accent/30 placeholder:text-ui-text-subtle transition-colors"
          />
        </div>
      )}

      {/* Dropdown — portal sobre body para escapar del overflow:hidden del modal */}
      {open && showInput && !loading && dropdownRect && createPortal(
        <div
          id="relation-portal"
          style={{ position: 'fixed', top: dropdownRect.top, left: dropdownRect.left, width: dropdownRect.width, zIndex: 9999 }}
          className="max-h-56 overflow-y-auto bg-ui-surface-2 border border-ui-border rounded-md shadow-xl"
        >
          {filtered.length === 0 ? (
            <p className="px-3 py-2.5 text-sm text-ui-text-subtle">
              {options.length === 0
                ? 'Aún no hay opciones disponibles.'
                : 'No encontramos coincidencias. Prueba con otro nombre.'}
            </p>
          ) : (
            filtered.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => pick(o.id)}
                className="flex items-center justify-between w-full text-left px-3 py-2 text-sm text-ui-text-muted hover:text-ui-text hover:bg-ui-text/[0.06] transition-colors"
              >
                <span className="truncate">{itemTitle(o, locale)}</span>
                {o.status === 'draft' && (
                  <span className="ml-2 shrink-0 text-sm text-ui-text-subtle">Borrador</span>
                )}
              </button>
            ))
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
